/**
 * Regional behaviour lives behind this abstraction.
 *
 * Two rule sets ship today, both neutral: no Riichi / Hong Kong / Taiwanese /
 * MCR scoring is implemented or implied. Adding a regional system later means
 * adding a RuleSet (and a scoring hook), not editing the engine.
 */

import { analyzeStandardHand, findWaits } from "./hand.js";
import { buildTileSet } from "./tiles.js";
import type { Tile, TileKind } from "./tiles.js";
import type { ClaimType, WinningHand } from "./types.js";

export interface RuleSet {
  id: string;
  name: string;
  description: string;
  playerCount: 4;
  /** Concealed tiles held between turns, before any meld is exposed. */
  handSize: number;
  /** Extra tile the dealer holds at the start of the game. */
  dealerExtraTiles: number;
  /** Tiles held back as kong replacements. */
  deadWallSize: number;

  /** Claiming a discard at all (chow / pong / kong / ron). */
  allowsClaims: boolean;
  /** Winning on another player's discard. */
  allowsWinOnDiscard: boolean;
  /** Chow only for the player who would draw next (the discarder's right). */
  chowFromNextPlayerOnly: boolean;
  allowsConcealedKong: boolean;
  /** Upgrading an exposed pong to a kong with the fourth tile. */
  allowsAddedKong: boolean;
  /** Winning on the tile someone adds to a kong. Not implemented yet. */
  allowsRobbingKong: boolean;
  /** Where the replacement tile after a kong comes from. */
  kongReplacement: "dead-wall" | "none";
  /** Highest priority first, used when several players claim the same discard. */
  claimPriority: readonly ClaimType[];
  /** Which win counts when several players claim the same discard. */
  multipleWinners: "closest-to-discarder";
  /** No points are awarded: the scoring system is intentionally unspecified. */
  scoring: "none";

  /** The ordered, unshuffled tile set for one game. */
  buildWall(): Tile[];
  /** Null when the tiles are not a complete winning hand. */
  analyzeWin(concealed: readonly TileKind[], declaredMelds: number): WinningHand | null;
  /** Tiles that would complete the hand on the next draw. */
  findWaits(concealed: readonly TileKind[], declaredMelds: number): TileKind[];
}

const SHARED = {
  playerCount: 4,
  handSize: 13,
  dealerExtraTiles: 1,
  multipleWinners: "closest-to-discarder",
  scoring: "none",
  buildWall: buildTileSet,
  analyzeWin: (concealed: readonly TileKind[], declaredMelds: number) =>
    analyzeStandardHand(concealed, declaredMelds),
  findWaits: (concealed: readonly TileKind[], declaredMelds: number) =>
    findWaits(concealed, declaredMelds),
} as const;

/** Milestone 1 configuration: draw and discard only. Kept as a regression target. */
export const CLASSICAL_NEUTRAL_V0: RuleSet = {
  ...SHARED,
  id: "classical-neutral-v0",
  name: "Classical (neutral, no claims)",
  description:
    "136 tiles, 4 players, draw and discard only, win on a standard hand of four " +
    "sets plus a pair by self-draw. No claims, no dead wall, no regional scoring.",
  deadWallSize: 0,
  allowsClaims: false,
  allowsWinOnDiscard: false,
  chowFromNextPlayerOnly: true,
  allowsConcealedKong: false,
  allowsAddedKong: false,
  allowsRobbingKong: false,
  kongReplacement: "none",
  claimPriority: ["ron", "kong", "pong", "chow"],
};

/** Milestone 2 default: the full traditional turn flow with claims. */
export const CLASSICAL_NEUTRAL_V1: RuleSet = {
  ...SHARED,
  id: "classical-neutral-v1",
  name: "Classical (neutral, with claims)",
  description:
    "136 tiles, 4 players, draw and discard plus chow / pong / kong claims and " +
    "winning on a discard. 14-tile dead wall for kong replacements. Standard " +
    "winning shape only (four sets plus a pair) and no regional scoring.",
  deadWallSize: 14,
  allowsClaims: true,
  allowsWinOnDiscard: true,
  chowFromNextPlayerOnly: true,
  allowsConcealedKong: true,
  allowsAddedKong: true,
  allowsRobbingKong: false,
  kongReplacement: "dead-wall",
  claimPriority: ["ron", "kong", "pong", "chow"],
};

export const DEFAULT_RULE_SET_ID = CLASSICAL_NEUTRAL_V1.id;

const RULE_SETS: Record<string, RuleSet> = {
  [CLASSICAL_NEUTRAL_V0.id]: CLASSICAL_NEUTRAL_V0,
  [CLASSICAL_NEUTRAL_V1.id]: CLASSICAL_NEUTRAL_V1,
};

export function getRuleSet(id: string = DEFAULT_RULE_SET_ID): RuleSet {
  const ruleSet = RULE_SETS[id];
  if (!ruleSet) throw new Error(`Unknown rule set: ${id}`);
  return ruleSet;
}

export function listRuleSets(): RuleSet[] {
  return Object.values(RULE_SETS);
}

/** Lower number = higher priority. Unknown claim types sort last. */
export function claimRank(ruleSet: RuleSet, claim: ClaimType): number {
  const index = ruleSet.claimPriority.indexOf(claim);
  return index === -1 ? ruleSet.claimPriority.length : index;
}
